import React from 'react'
import { Link } from '@tanstack/react-router'

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'black' | 'outline-purple' | 'onboarding' | 'thinGreen' | 'outline-black'
type ButtonSize = 'small' | 'medium' | 'large' | 'xlarge'

type AnchorButtonProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string
  to?: never
}
type RouterButtonProps = {
  to: string
  href?: never
} & React.AnchorHTMLAttributes<HTMLAnchorElement>
type NativeButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  href?: never
  to?: never
}

type ButtonProps = {
  variant?: ButtonVariant
  size?: ButtonSize
  isLoading?: boolean
  fullWidth?: boolean
  children: React.ReactNode
  iconAfter?: React.ReactNode; // Pass icon with desired size
  iconSize?: number | string; // (No longer used, for backward compatibility)
} & (AnchorButtonProps | RouterButtonProps | NativeButtonProps)

const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    props,
    ref
  ) => {
    const {
      variant = 'primary',
      size = 'medium',
      isLoading = false,
      fullWidth = false,
      children,
      className = '',
      iconAfter,
      iconSize = 20,
      ...rest
    } = props

    const baseStyles = 'flex justify-center items-center text-center font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer disabled:cursor-not-allowed'
    const variantStyles = {
      primary: 'bg-violet-600 text-white hover:bg-violet-500 focus:ring-purple-500 disabled:bg-gray-300',
      secondary: 'bg-gray-300 text-secondary hover:bg-gray-400 focus:ring-gray-500 disabled:bg-gray-100',
      danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-gray-300',
      ghost: 'bg-transparent text-secondary hover:bg-gray-100 focus:ring-gray-500 disabled:bg-gray-100',
      black: 'bg-black text-white hover:bg-gray-900 focus:ring-black disabled:bg-gray-300',
      onboarding: 'font-light bg-black text-white hover:bg-gray-900 focus:ring-black disabled:bg-gray-300',
      'outline-purple': 'bg-transparent text-black border-2 border-violet-400 hover:border-violet-400 hover:bg-violet-100 focus:ring-violet-400 disabled:bg-gray-100',
      'outline-black': 'bg-transparent text-black border-1 border-gray-200 hover:border-violet-400 hover:bg-violet-200 hover:text-gray-800 focus:ring-violet-400 disabled:bg-gray-100',
      thinGreen: 'bg-green-100 border border-green-300 text-green-900 px-3 py-1 rounded hover:bg-green-200 focus:ring-green-400 disabled:bg-green-50',
    }
    const sizeStyles = {
      small: 'px-3 py-1.5 text-sm',
      medium: 'px-4 py-2 text-md',
      large: 'px-6 py-3 text-lg',
      xlarge: 'px-8 py-4 text-xl',
    }
    const widthStyle = fullWidth ? 'w-full' : ''
    const classes = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyle} ${className}`

    // Render iconAfter as-is. If you want to control size, pass the icon with the desired size prop.
    const icon = iconAfter;

    if ('to' in props && props.to) {
      const { to, ...linkProps } = rest as RouterButtonProps
      return (
        <Link
          to={to}
          className={classes}
          aria-disabled={isLoading}
          tabIndex={isLoading ? -1 : 0}
          ref={ref as React.Ref<HTMLAnchorElement>}
          role="button"
          {...linkProps}
        >
          {isLoading ? 'Loading...' : children}
          {icon && <span className="ml-2 flex items-center">{icon}</span>}
        </Link>
      )
    }

    if ('href' in props && props.href) {
      const { href, ...anchorProps } = rest as AnchorButtonProps
      return (
        <a
          href={href}
          className={classes + ' no-underline'}
          aria-disabled={isLoading}
          tabIndex={isLoading ? -1 : 0}
          ref={ref as React.Ref<HTMLAnchorElement>}
          role="button"
          {...anchorProps}
        >
          {isLoading ? 'Loading...' : children}
          {icon && <span className="ml-2 flex items-center">{icon}</span>}
        </a>
      )
    }

    const { disabled, type, ...buttonProps } = rest as NativeButtonProps
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={classes}
        disabled={isLoading || disabled}
        type={type}
        {...buttonProps}
      >
        {isLoading ? 'Loading...' : children}
        {icon && <span className="ml-2 flex items-center">{icon}</span>}
      </button>
    )
  }
)

export default Button 